import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, InputBase, Popover, Stack, Tooltip, Typography, CircularProgress } from '@mui/material';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ReplyRoundedIcon from '@mui/icons-material/ReplyRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';
import EmojiEmotionsOutlinedIcon from '@mui/icons-material/EmojiEmotionsOutlined';
import MicRoundedIcon from '@mui/icons-material/MicRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import toast from 'react-hot-toast';
import { PRIMARY, PRIMARY_LIGHT, PRIMARY_BORDER } from '../../theme';
import { ALLOWED_ATTACHMENT_ACCEPT, MAX_FILE_SIZE, MAX_FILE_MB, formatFileSize, formatDuration } from './chatHelpers';
import EmojiPickerPanel from './EmojiPickerPanel';

export default function MessageComposer({
    disabled, disabledReason, sending, replyTo, onCancelReply,
    editingMessage, onCancelEdit, onSend, onSaveEdit, onTyping,
}) {
    const [text, setText] = useState('');
    const [file, setFile] = useState(null);
    const [emojiAnchor, setEmojiAnchor] = useState(null);
    const [isRecording, setIsRecording] = useState(false);
    const [recordSeconds, setRecordSeconds] = useState(0);
    const fileRef = useRef(null);
    const inputRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const recordStreamRef = useRef(null);
    const recordTimerRef = useRef(null);
    const recordCancelledRef = useRef(false);

    useEffect(() => {
        if (editingMessage) {
            setText(editingMessage.messageText || '');
            setFile(null);
            inputRef.current?.focus();
        }
    }, [editingMessage]);

    useEffect(() => {
        if (replyTo) inputRef.current?.focus();
    }, [replyTo]);

    const pickFile = (e) => {
        const f = e.target.files?.[0];
        e.target.value = '';
        if (!f) return;
        if (f.size > MAX_FILE_SIZE) {
            toast.error(`Attachments must be ${MAX_FILE_MB} MB or smaller.`);
            return;
        }
        setFile(f);
    };

    const canSubmit = editingMessage ? text.trim().length > 0 : (text.trim().length > 0 || !!file);

    const submit = () => {
        if (!canSubmit || sending) return;
        if (editingMessage) {
            onSaveEdit(text.trim());
        } else {
            onSend(text.trim(), file);
            setText('');
            setFile(null);
        }
    };

    const onKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submit();
        }
    };

    const insertEmoji = (emoji) => {
        setText((t) => t + emoji);
        inputRef.current?.focus();
    };

    // ---- voice-note recording (MediaRecorder) ----
    const stopRecordTimer = () => {
        clearInterval(recordTimerRef.current);
        recordTimerRef.current = null;
    };

    const startRecording = async () => {
        if (isRecording) return;
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            recordStreamRef.current = stream;
            const recorder = new MediaRecorder(stream);
            audioChunksRef.current = [];
            recordCancelledRef.current = false;

            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) audioChunksRef.current.push(e.data);
            };
            recorder.onstop = () => {
                stream.getTracks().forEach((t) => t.stop());
                recordStreamRef.current = null;
                if (recordCancelledRef.current) return;
                const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                if (!blob.size) return;
                const audioFile = new File([blob], `voice_${Date.now()}.weba`, { type: 'audio/webm' });
                onSend('', audioFile);
            };

            mediaRecorderRef.current = recorder;
            recorder.start();
            setIsRecording(true);
            setRecordSeconds(0);
            recordTimerRef.current = setInterval(() => setRecordSeconds((s) => s + 1), 1000);
        } catch {
            toast.error('Microphone access denied. Please allow microphone permission to record voice messages.');
        }
    };

    const stopRecording = (cancelled) => {
        recordCancelledRef.current = cancelled;
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
        }
        stopRecordTimer();
        setIsRecording(false);
        setRecordSeconds(0);
    };

    useEffect(() => () => {
        // Safety net: release the mic and stop the timer if the composer
        // unmounts mid-recording (e.g. the user switches groups).
        stopRecordTimer();
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            recordCancelledRef.current = true;
            mediaRecorderRef.current.stop();
        }
        recordStreamRef.current?.getTracks().forEach((t) => t.stop());
    }, []);

    if (disabled) {
        return (
            <Box sx={{ p: 1.6, borderTop: '1px solid #EEF0F6', bgcolor: '#F8F8FC', textAlign: 'center' }}>
                <Typography sx={{ fontSize: 12.5, color: '#94A3B8', fontWeight: 600 }}>
                    {disabledReason || 'You can no longer send messages in this group.'}
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ borderTop: '1px solid #EEF0F6', bgcolor: '#fff' }}>
            {replyTo && !editingMessage && (
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', px: 1.6, py: 0.9, bgcolor: PRIMARY_LIGHT, borderBottom: `1px solid ${PRIMARY_BORDER}` }}>
                    <ReplyRoundedIcon sx={{ fontSize: 16, color: PRIMARY }} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: PRIMARY }}>{replyTo.senderName}</Typography>
                        <Typography sx={{ fontSize: 12, color: '#5B6472' }} noWrap>{replyTo.messageText || (replyTo.attachmentName ? `📎 ${replyTo.attachmentName}` : '')}</Typography>
                    </Box>
                    <IconButton size="small" onClick={onCancelReply}><CloseRoundedIcon sx={{ fontSize: 16 }} /></IconButton>
                </Stack>
            )}
            {editingMessage && (
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', px: 1.6, py: 0.9, bgcolor: '#FFFBEB', borderBottom: '1px solid #FCD9A5' }}>
                    <EditRoundedIcon sx={{ fontSize: 16, color: '#B45309' }} />
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#B45309', flex: 1 }}>Editing message</Typography>
                    <IconButton size="small" onClick={onCancelEdit}><CloseRoundedIcon sx={{ fontSize: 16 }} /></IconButton>
                </Stack>
            )}
            {file && !editingMessage && (
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', px: 1.6, py: 0.9, bgcolor: '#F8FAFC', borderBottom: '1px solid #EEF0F6' }}>
                    <InsertDriveFileRoundedIcon sx={{ fontSize: 16, color: PRIMARY }} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#0F172A' }} noWrap>{file.name}</Typography>
                        <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>{formatFileSize(file.size)}</Typography>
                    </Box>
                    <IconButton size="small" onClick={() => setFile(null)}><CloseRoundedIcon sx={{ fontSize: 16 }} /></IconButton>
                </Stack>
            )}

            {isRecording ? (
                <Stack direction="row" spacing={1.2} sx={{ alignItems: 'center', p: 1.2 }}>
                    <Tooltip arrow title="Cancel">
                        <IconButton onClick={() => stopRecording(true)} sx={{ color: '#E11D48' }}>
                            <DeleteOutlineRoundedIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                    </Tooltip>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flex: 1, px: 1.6, py: 0.9, borderRadius: '999px', bgcolor: '#FEF2F2' }}>
                        <MicRoundedIcon sx={{ fontSize: 18, color: '#E11D48' }} />
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#E11D48', minWidth: 40 }}>{formatDuration(recordSeconds)}</Typography>
                        <Typography sx={{ fontSize: 12, color: '#E11D48', opacity: 0.75 }}>Recording…</Typography>
                    </Stack>
                    <Tooltip arrow title="Send recording">
                        <IconButton onClick={() => stopRecording(false)} sx={{ width: 40, height: 40, bgcolor: PRIMARY, color: '#fff', '&:hover': { bgcolor: '#0E4A75' } }}>
                            <SendRoundedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                </Stack>
            ) : (
                <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-end', p: 1.2 }}>
                    {!editingMessage && (
                        <>
                            <input ref={fileRef} type="file" hidden accept={ALLOWED_ATTACHMENT_ACCEPT} onChange={pickFile} />
                            <Tooltip arrow title="Attach a file">
                                <IconButton onClick={() => fileRef.current?.click()} sx={{ color: '#94A3B8', '&:hover': { color: PRIMARY, bgcolor: PRIMARY_LIGHT } }}>
                                    <AttachFileRoundedIcon sx={{ fontSize: 20 }} />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                    <Tooltip arrow title="Emoji">
                        <IconButton onClick={(e) => setEmojiAnchor(e.currentTarget)} sx={{ color: emojiAnchor ? PRIMARY : '#94A3B8', '&:hover': { color: PRIMARY, bgcolor: PRIMARY_LIGHT } }}>
                            <EmojiEmotionsOutlinedIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                    </Tooltip>
                    <Popover
                        open={Boolean(emojiAnchor)}
                        anchorEl={emojiAnchor}
                        onClose={() => setEmojiAnchor(null)}
                        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    >
                        <EmojiPickerPanel onSelect={insertEmoji} />
                    </Popover>
                    <Box sx={{ flex: 1, minWidth: 0, bgcolor: '#F8FAFC', border: '1px solid #E5E7EB', borderRadius: '10px', px: 1.6, py: 1 }}>
                        <InputBase
                            inputRef={inputRef}
                            placeholder="Type a message…"
                            value={text}
                            multiline
                            maxRows={5}
                            onChange={(e) => { setText(e.target.value); onTyping?.(); }}
                            onKeyDown={onKeyDown}
                            sx={{ fontSize: 13.5, width: '100%' }}
                        />
                    </Box>
                    {canSubmit || editingMessage ? (
                        <IconButton
                            onClick={submit}
                            disabled={!canSubmit || sending}
                            sx={{
                                width: 40, height: 40, bgcolor: canSubmit ? PRIMARY : '#E2E8F0', color: '#fff',
                                '&:hover': { bgcolor: canSubmit ? '#0E4A75' : '#E2E8F0' },
                                '&.Mui-disabled': { color: '#fff', bgcolor: '#E2E8F0' },
                            }}
                        >
                            {sending ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SendRoundedIcon sx={{ fontSize: 18 }} />}
                        </IconButton>
                    ) : (
                        <Tooltip arrow title="Record a voice message">
                            <IconButton onClick={startRecording} sx={{ width: 40, height: 40, bgcolor: PRIMARY, color: '#fff', '&:hover': { bgcolor: '#0E4A75' } }}>
                                <MicRoundedIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Stack>
            )}
        </Box>
    );
}
